/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type {
  ReactElement,
} from 'react';
import styled from 'styled-components';

import type {
  ControlProps,
  GroupBase,
} from 'react-select';

import type {
  ReactSelectExtendedProps,
} from '../ReactSelectExtended';
import type {
  OptionTypeBase,
} from '../types';

export const StyledControl = styled.div<{
  $small?: boolean;
  $isDisabled?: boolean;
  $isFocused?: boolean;
  $hasWarning?: boolean;
  $hasError?: boolean;
  $menuIsOpen?: boolean;
  $viewMode: boolean;
}>(({
  $small,
  $isDisabled,
  $isFocused,
  $hasWarning,
  $hasError,
  $menuIsOpen,
  $viewMode,

  theme: {
    colors,
    input,
  },
}) => ({
  boxSizing: 'border-box',
  alignItems: 'center',
  cursor: $isDisabled ? 'default' : 'pointer',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  position: 'relative',
  transition: 'all 100ms',
  outlineWidth: ($isFocused || $menuIsOpen || $hasError)
    ? input.BORDER_WIDTH_FOCUS
    : input.BORDER_WIDTH,
  outlineStyle: 'solid',
  outlineColor: $viewMode ? colors[input.BORDER_COLOR] : $isDisabled
    ? colors.NEUTRAL_INACTIVE
    : (
      ($isFocused || $menuIsOpen)
        ? colors[input.BORDER_COLOR_FOCUS]
        : (
          $hasError
            ? colors[input.BORDER_COLOR_ERROR]
            : (
              $hasWarning
                ? colors[input.BORDER_COLOR_WARNING]
                : colors[input.BORDER_COLOR]
            )
        )
    ),

  backgroundColor: $isDisabled
    ? colors[input.BG_COLOR_DISABLED]
    : (
      ($isFocused || $menuIsOpen)
        ? colors[input.BG_COLOR_FOCUS]
        : (
          $hasError
            ? colors[input.BG_COLOR_ERROR]
            : (
              $hasWarning
                ? colors[input.BG_COLOR_WARNING]
                : colors[input.BG_COLOR]
            )
        )
    ),

  minHeight: $small ? input.HEIGHT_SMALL : input.HEIGHT,
  borderRadius: input.BORDER_RADIUS,

  '&:hover': ($isDisabled || $isFocused || $menuIsOpen) ? undefined : {
    outlineColor: colors[input.BORDER_COLOR_HOVER],
    outlineWidth: input.BORDER_WIDTH_HOVER,
  },
}));

export function Control<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(props: ControlProps<OptionType, IsMulti, Group>): ReactElement {
  const {
    children,
    isDisabled,
    isFocused,
    innerRef,
    innerProps,
    selectProps,
    selectProps: {
      menuIsOpen,
      name = '',
    },
    className,
  } = props;

  const {
    hasWarning,
    hasError,
    small,
    viewMode = false,
  } = selectProps as ReactSelectExtendedProps<OptionType, IsMulti, Group>;

  return (
    <StyledControl
      ref={innerRef as any}
      className={className}
      $small={small}
      $isDisabled={isDisabled}
      $isFocused={isFocused}
      $hasWarning={hasWarning}
      $hasError={hasError}
      $menuIsOpen={menuIsOpen}
      $viewMode={viewMode}
      data-n3__select-container={name}
      {...innerProps}
    >
      {children}
    </StyledControl>
  );
}
